import type { Rank } from "./cards.js";
import { RANK_2, RANK_10 } from "./cards.js";

/**
 * Guandan varies by region in ways that change real decisions at the table, so
 * every contested rule is a flag rather than a hard-coded assumption. See
 * RULES.md for what each option means and why the defaults were chosen.
 */
export interface Ruleset {
  readonly id: string;
  readonly label: string;

  /** Where 同花顺 (straight flush) sits in the bomb ladder. */
  readonly straightFlushRank: "aboveFiveBomb" | "aboveSixBomb";

  /** Tribute and return tribute (进贡 / 还贡) between hands. */
  readonly tribute: boolean;
  /** Holding both big jokers lets the tributing side refuse (抗贡). */
  readonly resistTribute: boolean;
  /** Highest preferred return rank; any card is allowed if the hand has none at or below it. */
  readonly maxReturnTributeRank: Rank;
  /** Who leads the first trick of a hand where tribute was paid. */
  readonly tributeLead: "tributePayer" | "handWinner";

  /**
   * What a team sitting at A must achieve to take the match.
   *
   * `partnerTopThree` is the 2025 world competition requirement: the A-side
   * takes first *and* its partner finishes second or third. It is deliberately
   * not a boolean — "any win" and "双下 only" are the two rules it sits between,
   * and both are played somewhere.
   */
  readonly aWinRequirement: "anyWin" | "partnerTopThree" | "doubleDown";

  /**
   * House rule. Failed attempts at level A before the team drops back; 0
   * disables the penalty entirely, which is what competition play does.
   */
  readonly aAttempts: number;
  /** Level a team falls back to after exhausting its attempts at A. */
  readonly aFailFallbackLevel: Rank;

  /** Level cards outrank aces inside n-of-a-kind bombs as well as singles. */
  readonly levelElevatesInBombs: boolean;
  /** Ace may act low in straights and runs (A-2-3-4-5). */
  readonly aceLowRuns: boolean;
  /** Jokers may fill the pair half of a full house. */
  readonly jokersInFullHouse: boolean;

  /** Per-turn clock in ms. 0 disables the clock. */
  readonly turnMs: number;
}

/**
 * The default table: the 2025 world Guandan competition rules.
 * https://gawsf.b-cdn.net/files/世界掼牌（掼蛋）竞赛规则（2025）.pdf
 */
export const STANDARD: Ruleset = {
  id: "standard",
  label: "Competition",
  straightFlushRank: "aboveFiveBomb",
  tribute: true,
  resistTribute: true,
  maxReturnTributeRank: RANK_10,
  tributeLead: "tributePayer",
  aWinRequirement: "partnerTopThree",
  aAttempts: 0,
  aFailFallbackLevel: RANK_2,
  levelElevatesInBombs: true,
  aceLowRuns: true,
  jokersInFullHouse: false,
  turnMs: 25_000,
};

/**
 * Casual rules for players meeting Guandan for the first time. Tribute and the
 * A-attempt penalty are the two systems that reliably confuse newcomers without
 * teaching them anything about card play, so both are off.
 */
export const CASUAL: Ruleset = {
  ...STANDARD,
  id: "casual",
  label: "Casual",
  tribute: false,
  resistTribute: false,
  aWinRequirement: "anyWin",
  turnMs: 40_000,
};

/**
 * Competition rules plus the regional three-attempts-at-A penalty. Common at
 * home tables, absent from the competition rules, so it lives here rather than
 * in the default.
 */
export const HOUSE: Ruleset = {
  ...STANDARD,
  id: "house",
  label: "House",
  aAttempts: 3,
  aFailFallbackLevel: RANK_2,
};

/**
 * The daily deal: the single hand that decides a match.
 *
 * Both teams start at A (see `MatchOptions.levels`), so winning this hand wins
 * the match and the engine ends it through its own `matchOver` path rather than
 * through a second notion of "finished" invented for one game mode.
 *
 * Tribute never applies to a first hand — there is no previous one to owe
 * anything from — so the most confusing rule in Guandan is absent here without
 * having to be switched off. The A-attempt penalty is disabled because there is
 * no later hand for a failed attempt to matter in.
 */
export const DAILY: Ruleset = {
  ...STANDARD,
  id: "daily",
  label: "Daily deal",
  aWinRequirement: "anyWin",
  aAttempts: 0,
};

export const RULESETS: Record<string, Ruleset> = {
  [STANDARD.id]: STANDARD,
  [CASUAL.id]: CASUAL,
  [HOUSE.id]: HOUSE,
  [DAILY.id]: DAILY,
};

export function rulesetById(id: string): Ruleset {
  return RULESETS[id] ?? STANDARD;
}
